import { create } from 'zustand'
import Tracker from '@openreplay/tracker'

interface OpenReplayUserMetadata {
  userId: string
  email?: string
  telegramId?: number | null
  telegramUsername?: string | null
}

interface OpenReplayState {
  tracker: Tracker | null
  isInitialized: boolean
  isStarted: boolean
  init: () => void
  start: (metadata: OpenReplayUserMetadata) => Promise<void>
  stop: () => void
  setUserMetadata: (metadata: OpenReplayUserMetadata) => void
  reset: () => void
}

export const useOpenReplayStore = create<OpenReplayState>((set, get) => ({
  tracker: null,
  isInitialized: false,
  isStarted: false,

  init: () => {
    if (get().isInitialized) return
    if (import.meta.env.VITE_OPENREPLAY_ENABLED !== 'true') return

    const projectKey = import.meta.env.VITE_OPENREPLAY_PROJECT_KEY
    const ingestPoint = import.meta.env.VITE_OPENREPLAY_INGEST_POINT

    // Если env переменные не заданы - не инициализируем
    if (!projectKey || !ingestPoint) {
      console.log('[OpenReplay] Missing configuration, skipping')
      set({ isInitialized: true })
      return
    }

    try {
      const tracker = new Tracker({
        projectKey,
        ingestPoint,
        __DISABLE_SECURE_MODE: import.meta.env.DEV,
        // Явно указываем assistSocketHost чтобы предотвратить запросы на локальную сеть
        // Это связано с Chrome 142+ Local Network Access (LNA) policy
        assistSocketHost: ingestPoint,
        // Отключаем сбор метрик производительности, которые могут использовать WebRTC
        capturePerformance: false,
      })
      set({ tracker, isInitialized: true })
      console.log('[OpenReplay] Tracker initialized')
    } catch (error) {
      console.error('[OpenReplay] Init error:', error)
      set({ isInitialized: true })
    }
  },

  start: async (metadata) => {
    const { tracker, isStarted } = get()
    if (!tracker || isStarted) return

    try {
      const result = await tracker.start({
        userID: metadata.userId,
        metadata: {
          email: metadata.email || '',
          telegram_id: metadata.telegramId ? String(metadata.telegramId) : '',
          telegram_username: metadata.telegramUsername || '',
        },
      })

      // Проверяем результат старта
      if (result.success) {
        set({ isStarted: true })
        console.log('[OpenReplay] Session started:', {
          sessionID: result.sessionID,
          userId: metadata.userId,
          email: metadata.email,
        })
      } else {
        console.error('[OpenReplay] Start failed:', result.reason)
      }
    } catch (error) {
      console.error('[OpenReplay] Start error:', error)
    }
  },

  stop: () => {
    const { tracker, isStarted } = get()
    if (!tracker || !isStarted) return
    tracker.stop()
    set({ isStarted: false })
    console.log('[OpenReplay] Session stopped')
  },

  setUserMetadata: (metadata) => {
    const { tracker, isStarted } = get()
    if (!tracker || !isStarted) {
      console.log('[OpenReplay] Cannot set metadata - tracker not started')
      return
    }

    // Обновляем метаданные в уже запущенной сессии
    tracker.setUserID(metadata.userId)
    if (metadata.email) {
      tracker.setMetadata('email', metadata.email)
    }
    if (metadata.telegramId) {
      tracker.setMetadata('telegram_id', String(metadata.telegramId))
    }
    if (metadata.telegramUsername) {
      tracker.setMetadata('telegram_username', metadata.telegramUsername)
    }
    console.log('[OpenReplay] Metadata updated:', metadata)
  },

  reset: () => {
    const { tracker } = get()
    if (tracker) tracker.stop()
    set({ tracker: null, isInitialized: false, isStarted: false })
  },
}))
